import { FC, useMemo } from 'react';
import omit from 'lodash-es/omit';

import { FamilyGroup } from '@gn-audio/jss-core';
import { Option } from '@gn-audio/jss-core/common/models';

import { Filter } from 'api/jabraPublicApi';

import { CheckboxFilter } from './CheckboxFilter/CheckboxFilter';
import { ColorFilter } from './ColorFilter/ColorFilter';
import { RadioFilter } from './RadioFilter/RadioFilter';
import {
  buildSelectedFiltersBasedOnGroup,
  CustomizerFilterTypes,
  FilterCondition,
  filterGroups,
  FilterOperatorType,
  getApplicableFilters,
  getFilterType,
} from './customizerFilterHelpers';

export interface CustomizerFilterProps {
  groups: FamilyGroup[];
  onSelect: (group: FamilyGroup) => void;
  filters: Filter[];
  selectedGroup?: FamilyGroup;
}

export const CustomizerGroupFilter: FC<CustomizerFilterProps> = ({
  groups,
  filters,
  onSelect,

  selectedGroup,
}) => {
  const applicableFilters = useMemo(() => getApplicableFilters(groups, filters), [groups, filters]);

  const selectedFilters = useMemo(
    () => buildSelectedFiltersBasedOnGroup(applicableFilters, selectedGroup),
    [applicableFilters, selectedGroup]
  );

  const handleSelectFilterOption = (
    filterName: string,
    option: Option,
    operator: FilterOperatorType = FilterOperatorType.include
  ) => {
    let newSelectedFilters;
    if (!option) {
      newSelectedFilters = omit(selectedFilters, filterName);
    } else {
      newSelectedFilters = { ...selectedFilters, [filterName]: { option, operator } };
    }
    applyFilter(newSelectedFilters);
  };

  const applyFilter = (filters: Record<string, FilterCondition>) => {
    const matchingGroups = filterGroups(groups, filters);

    const [matchingGroup] = matchingGroups;
    if (selectedGroup === matchingGroup) {
      return;
    }

    if (!matchingGroup) {
      return;
    }
    onSelect(matchingGroup);
  };

  return (
    <div className="flex flex-col gap-6">
      {applicableFilters.map((filter) => {
        const options = filter.options.map((option) => {
          const theoreticalFilter = {
            ...selectedFilters,
            [option.key]: { option, operator: FilterOperatorType.include },
          };
          const matchingGroups = filterGroups(groups, theoreticalFilter);
          return { ...option, disabled: !matchingGroups.length };
        });
        filter = { ...filter, options };

        const { option: selectedOption, operator } = selectedFilters[filter.name] || {};

        const renderFilterType = () => {
          switch (getFilterType(filter)) {
            case CustomizerFilterTypes.ColorPicker:
              return (
                <ColorFilter
                  options={filter.options}
                  selected={selectedOption}
                  onSelect={(option) => handleSelectFilterOption(filter.name, option)}
                />
              );
            case CustomizerFilterTypes.Checkbox:
              return (
                <CheckboxFilter
                  filter={filter}
                  option={selectedOption}
                  checked={operator === FilterOperatorType.include}
                  onChange={(include) =>
                    handleSelectFilterOption(
                      filter.name,
                      selectedOption,
                      include ? FilterOperatorType.include : FilterOperatorType.exclude
                    )
                  }
                />
              );
            case CustomizerFilterTypes.RadioButtons:
              return (
                <RadioFilter
                  filter={filter}
                  selected={selectedOption}
                  onSelect={(option) => handleSelectFilterOption(filter.name, option)}
                />
              );
            default:
              return null;
          }
        };
        return (
          <div
            key={filter.name}
            className="border-gray-50 [&:not(:last-child)]:border-b [&:not(:last-child)]:pb-6"
          >
            <span className="heading-6 mb-4 block text-gray-900">{filter.title}</span>
            {renderFilterType()}
          </div>
        );
      })}
    </div>
  );
};
